import { useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { Budget, BudgetColumn, BudgetViewType } from "./types";
import EmptyBudgets from "./emptyBudgets";
import QuadrosSidebar from "./components/QuadrosSidebar";
import KanbanColumnsGrid from "./components/KanbanColumnsGrid";
import KanbanColumnHeader from "./components/KanbanColumnHeader";

interface KanbanBoardProps {
  budgets: Budget[];
  viewType: BudgetViewType;
  onViewTypeChange: (viewType: BudgetViewType) => void;
  onViewHistory?: (budgetId: string) => void;
  onAction?: (budgetId: string, action: string) => void;
  persona?: string;
}

const KanbanBoard = ({
  budgets,
  viewType,
  onViewTypeChange,
  onViewHistory,
  onAction,
  persona,
}: KanbanBoardProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px) and (min-width: 769px)");
  const isManager = persona === "MANAGER";
  
  const columns: { key: BudgetColumn; label: string }[] = isManager
    ? [
        { key: "enviado", label: "Enviados para análise" },
        { key: "aceito", label: "Aprovado pelo gestor" },
        { key: "recusado", label: "Recusado pelo gestor" },
      ]
    : [
        { key: "pendente", label: "Pendente de ação" },
        { key: "enviado", label: "Enviado" },
        { key: "concluido", label: "Concluído" },
      ];

  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const getBudgetsByColumn = (column: BudgetColumn): Budget[] => {
    return budgets.filter((budget) => budget.column === column);
  };

  const pendenteBudgets = getBudgetsByColumn("pendente");
  const enviadoBudgets = getBudgetsByColumn("enviado");
  const concluidoBudgets = getBudgetsByColumn("concluido");
  const aceitoBudgets = getBudgetsByColumn("aceito");
  const recusadoBudgets = getBudgetsByColumn("recusado");
  
  const maxRows = isManager
    ? Math.max(
        enviadoBudgets.length,
        aceitoBudgets.length,
        recusadoBudgets.length
      )
    : Math.max(
        pendenteBudgets.length,
        enviadoBudgets.length,
        concluidoBudgets.length
      );

  const getBudgetAtRow = (column: BudgetColumn, rowIndex: number): Budget | null => {
    const budgetsInColumn = getBudgetsByColumn(column);
    return budgetsInColumn[rowIndex] || null;
  };

  const showQuadros = persona === "CONSULTANT";

  if (budgets.length === 0) {
    return (
      <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4`}>
        {showQuadros && (
          <QuadrosSidebar
            viewType={viewType}
            onViewTypeChange={onViewTypeChange}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        )}
        <div className="flex-1 bg-white p-3">
          <EmptyBudgets />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4`}>
      {showQuadros && (
        <QuadrosSidebar
          viewType={viewType}
          onViewTypeChange={onViewTypeChange}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      )}

      <div className="flex-1 bg-white p-3">
        <div className="relative w-full select-none">
          <div
            ref={headerRef}
            className={`grid gap-0 ${
              isMobile
                ? "grid-cols-1"
                : isTablet
                  ? "grid-cols-3 overflow-x-auto"
                  : "grid-cols-3"
            }`}
          >
            {columns.map((column) => (
              <KanbanColumnHeader key={column.key} column={column} isMobile={isMobile} />
            ))}
          </div>

          <div
            ref={contentRef}
            className={`${isMobile ? "overflow-y-auto" : "overflow-y-auto"} ${isTablet ? "overflow-x-auto" : ""}`}
            style={{ maxHeight: isMobile ? "auto" : "calc(100vh - 400px)" }}
          >
            <KanbanColumnsGrid
              columns={columns}
              budgets={budgets}
              getBudgetsByColumn={getBudgetsByColumn}
              getBudgetAtRow={getBudgetAtRow}
              maxRows={maxRows}
              isMobile={isMobile}
              isTablet={isTablet}
              onViewHistory={onViewHistory}
              onAction={onAction}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanBoard;
